'use client'

import * as React from 'react'
import { Button } from '@repo/studio-design-system-ui-button'
import { ChevronDownIcon } from '@repo/studio-design-system-ui-icons/arrows/chevron-down'
import { CheckIcon } from '@repo/studio-design-system-ui-icons/general/check'
import { Globe01Icon } from '@repo/studio-design-system-ui-icons/maps-travel/globe-01'
import { useLocale, useTranslations } from 'next-intl'
import * as Popover from 'primitives/popover'
import { tw } from 'utils/classnames'

import { useLangCookie } from '../lib/use-lang-cookie'
import { localeLabels } from '../locales'
import type { Locale } from '../locales'
import { usePathname, useRouter } from '../navigation'

/**
 * Header language dropdown. Uses next-intl routing so switching locale keeps
 * the visitor on the same page with the correct `[locale]` prefix.
 */
export function LanguageSwitcher() {
  const locale = useLocale() as Locale
  const pathname = usePathname()
  const router = useRouter()
  const t = useTranslations('studio-desktop-landing-pages.lps.shared.nav')
  const [, setLangCookie] = useLangCookie()

  const [open, setOpen] = React.useState(false)

  function handleSelect(nextLocale: Locale) {
    setOpen(false)
    setLangCookie(nextLocale)
    router.replace(pathname, { locale: nextLocale })
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button
          type="button"
          variant="overlay"
          size="sm"
          aria-label={t('selectLanguage')}
          className={tw(
            'rounded-full border border-sd-border bg-transparent text-sd-ink shadow-none',
            'hover:bg-sd-raised active:bg-sd-raised',
            'focus-visible:bg-sd-raised disabled:bg-transparent',
          )}
        >
          <Globe01Icon className="size-[15px]" aria-hidden />
          {locale.toUpperCase()}
          <ChevronDownIcon
            className={tw('size-[11px] transition-transform', open && 'rotate-180')}
            aria-hidden
          />
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          className="min-w-[200px] max-h-[360px] overflow-y-auto rounded-[14px] border border-sd-border bg-theme_semantic_surface_pop-over_primary-background p-1.5 shadow-[0_20px_50px_rgba(19,19,21,0.18)] animate-saiDrop outline-none"
        >
          <ul role="listbox" aria-label={t('selectLanguage')}>
            {[...localeLabels].map(([code, label]) => {
              const selected = code === locale

              return (
                <li key={code} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    onClick={() => handleSelect(code)}
                    className={tw(
                      'flex w-full items-center gap-2.5 rounded-[9px] px-3 py-2 text-left transition-colors',
                      selected ? 'bg-sd-raised' : 'hover:bg-sd-raised',
                    )}
                  >
                    <span
                      className={tw(
                        'w-6 font-mono text-[11px] font-semibold uppercase',
                        selected ? 'text-sd-accent' : 'text-sd-slate-3',
                      )}
                    >
                      {code}
                    </span>
                    <span className="text-[14px] font-medium text-sd-ink">{label}</span>
                    {selected ? (
                      <CheckIcon
                        className="ml-auto size-[13px] shrink-0 text-sd-accent"
                        aria-hidden
                      />
                    ) : null}
                  </button>
                </li>
              )
            })}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
